import React, { useRef, useState } from "react";

// Client-facing AI assistant (Corpus B / Product Assistant, per the
// Intelligence Core's docs/DUAL_LLM_ARCHITECTURE.md). Ported from
// ElyonAuto's ChatWidget.tsx, restyled to match this codebase's plain
// inline-style convention (no Tailwind here). Uses fetch + a manual
// ReadableStream reader rather than EventSource, since EventSource can't
// send a custom Authorization header. Self-hosted CPU-only inference
// measures ~20s for a grounded answer on this box -- streaming makes that
// feel like a chat response arriving instead of a hung request.
const BASE_URL = process.env.REACT_APP_API_URL || "https://demo.queue.falits.com/api";

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const sessionIdRef = useRef(null);

  async function send() {
    const message = input.trim();
    if (!message || sending) return;
    setInput("");
    setSending(true);
    setMessages((prev) => [...prev, { role: "user", content: message }, { role: "assistant", content: "" }]);

    try {
      const token = localStorage.getItem("eq_access_token");
      const response = await fetch(`${BASE_URL}/ai/chat/stream/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          message,
          ...(sessionIdRef.current ? { session_id: sessionIdRef.current } : {}),
        }),
      });

      if (!response.body) throw new Error("No response body");
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const events = buffer.split("\n\n");
        buffer = events.pop() || "";

        for (const raw of events) {
          const line = raw.trim();
          if (!line.startsWith("data: ")) continue;
          const event = JSON.parse(line.slice("data: ".length));

          if (event.type === "session") {
            sessionIdRef.current = event.session_id;
          } else if (event.type === "chunk") {
            setMessages((prev) => {
              const next = [...prev];
              next[next.length - 1] = {
                role: "assistant",
                content: next[next.length - 1].content + event.content,
              };
              return next;
            });
          }
        }
      }
    } catch {
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = {
          role: "assistant",
          content: "I'm sorry, I couldn't process your question right now. Please try again.",
        };
        return next;
      });
    } finally {
      setSending(false);
    }
  }

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} style={styles.launcher} title="AI Assistant">
        💬
      </button>
    );
  }

  return (
    <div style={styles.panel}>
      <div style={styles.header}>
        <span style={styles.headerTitle}>EliyaQ Assistant</span>
        <button onClick={() => setOpen(false)} style={styles.closeBtn}>✕</button>
      </div>

      <div style={styles.messages}>
        {messages.length === 0 && (
          <p style={styles.emptyHint}>
            Ask me how to use EliyaQ -- issuing tickets, calling patients, managing stations, and more.
          </p>
        )}
        {messages.map((m, i) => (
          <div key={i} style={m.role === "user" ? styles.userBubble : styles.assistantBubble}>
            {m.content || (sending && i === messages.length - 1 ? "..." : "")}
          </div>
        ))}
      </div>

      <div style={styles.inputRow}>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          disabled={sending}
          placeholder="Ask a question..."
          style={styles.input}
        />
        <button onClick={send} disabled={sending || !input.trim()} style={styles.sendBtn}>
          ➤
        </button>
      </div>
    </div>
  );
}

const styles = {
  launcher: {
    position: "fixed", bottom: "20px", right: "20px", zIndex: 50,
    height: "48px", width: "48px", borderRadius: "50%",
    background: "#2563EB", color: "#fff", border: "none",
    boxShadow: "0 4px 12px rgba(0,0,0,0.3)", fontSize: "20px", cursor: "pointer",
  },
  panel: {
    position: "fixed", bottom: "20px", right: "20px", zIndex: 50,
    width: "320px", height: "440px",
    background: "rgba(10,16,35,0.98)", border: "1px solid rgba(37,99,235,0.2)",
    borderRadius: "10px", boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
    display: "flex", flexDirection: "column", overflow: "hidden",
  },
  header: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "10px 12px", borderBottom: "1px solid rgba(37,99,235,0.15)",
    background: "rgba(30,41,59,0.5)",
  },
  headerTitle: { fontSize: "13px", fontWeight: "600", color: "#E2E8F0" },
  closeBtn: { background: "none", border: "none", color: "#64748B", cursor: "pointer", fontSize: "13px" },
  messages: { flex: 1, overflowY: "auto", padding: "12px", display: "flex", flexDirection: "column", gap: "8px" },
  emptyHint: { fontSize: "12px", color: "#475569" },
  userBubble: {
    alignSelf: "flex-end", maxWidth: "85%", background: "#2563EB", color: "#fff",
    borderRadius: "8px", padding: "6px 10px", fontSize: "13px",
  },
  assistantBubble: {
    alignSelf: "flex-start", maxWidth: "85%", background: "rgba(30,41,59,0.8)", color: "#E2E8F0",
    borderRadius: "8px", padding: "6px 10px", fontSize: "13px", whiteSpace: "pre-wrap",
  },
  inputRow: { display: "flex", gap: "8px", padding: "10px", borderTop: "1px solid rgba(37,99,235,0.15)" },
  input: {
    flex: 1, background: "rgba(30,41,59,0.5)", border: "1px solid rgba(37,99,235,0.2)",
    borderRadius: "6px", padding: "7px 10px", fontSize: "13px", color: "#E2E8F0", outline: "none",
  },
  sendBtn: {
    height: "32px", width: "32px", flexShrink: 0, borderRadius: "6px",
    background: "#2563EB", color: "#fff", border: "none", cursor: "pointer",
  },
};
